import Dexie, {Table} from 'dexie';
import {SyncStatusItem} from '../interfaces/core/sync-status-item';
import {ServiceFacility} from '../interfaces/core/service-facility';
import {Customer} from '../interfaces/customers/customer';
import {WmWoundQuestion} from '../interfaces/wound-management/wm-wound-question';
import {WmWoundLocation} from '../interfaces/wound-management/wm-wound-location';
import {WmWoundFinishedReason} from '../interfaces/wound-management/wm-wound-finished-reason';
import {CdMdsArea} from '../interfaces/care/cd-rai/cd-mds-area';
import {CdAimEvaluationReason} from '../interfaces/care/cd-care-plan/cd-aim-evaluation-reason';
import {CdAimEvaluationScale} from '../interfaces/care/cd-care-plan/cd-aim-evaluation-scale';
import {CdRai} from '../interfaces/care/cd-rai/cd-rai';
import {CdMdsAnswer} from '../interfaces/care/cd-rai/cd-mds-answer';
import {SyncItem} from '../interfaces/core/sync-item';
import {WmWoundDocumentation} from '../interfaces/wound-management/wm-wound-documentation';
import {WmWoundDocumentationEntry} from '../interfaces/wound-management/wm-wound-documentation-entry';
import {WmWoundAnswer} from '../interfaces/wound-management/wm-wound-answer';
import {WmWoundImage} from '../interfaces/wound-management/wm-wound-image';
import {CdCarePlan} from '../interfaces/care/cd-care-plan/cd-care-plan';
import {CdAimEvaluation} from '../interfaces/care/cd-care-plan/cd-aim-evaluation';
import {CdAddedAim} from '../interfaces/care/cd-care-plan/cd-added-aim';
import {StorageItem} from '../interfaces/core/storage-item';
import {TableName} from '../enums/core/table-name';
import {CdAimEvaluationOrderOption} from '../interfaces/care/cd-care-plan/cd-aim-evaluation-order-option';
import {PrProcessReport} from '../interfaces/process-reports/pr-process-report';
import {ErrorLog} from '../interfaces/core/error-log';
import {EmployeeRoster} from '../interfaces/employee-rosters/employee-roster';
import {OpOperatingPlanItem} from '../interfaces/operating-plan/op-operating-plan-item';
import {SyncTime} from '../interfaces/core/sync-time';
import {Vehicle} from '../interfaces/vehicles/vehicle';
import {TtTravelEntry} from '../interfaces/time-tracking/tt-travel-entry';
import {TtActivityType} from '../interfaces/time-tracking/tt-activity-type';
import {Service} from '../interfaces/core/service';
import {TtActivityEntry} from '../interfaces/time-tracking/tt-activity-entry';
import {Employee} from '../interfaces/core/employee';
import {ReRelationType} from '../interfaces/relatives/re-relation-type';
import {TtWorkingTimeEntry} from '../interfaces/time-tracking/tt-working-time-entry';
import {TtWorkingTimeType} from '../interfaces/time-tracking/tt-working-time-type';
import {Product} from '../interfaces/product-management/product';
import {TtProductEntry} from '../interfaces/time-tracking/tt-product-entry';
import {Instruction} from '../interfaces/instructions/instruction';
import {CustomerDocumentType} from '../interfaces/customer-documents/customer-document-type';
import {CustomerDocument} from '../interfaces/customer-documents/customer-document';
import {ProdRequest} from '../interfaces/product-management/prod-request';
import {VitalSignContext} from '../interfaces/vital-signs/vital-sign-context';
import {VitalSignEntry} from '../interfaces/vital-signs/vital-sign-entry';
import {VitalSignThreshold} from '../interfaces/vital-signs/vital-sign-threshold';
import {VitalSignOption} from '../interfaces/vital-signs/vital-sign-option';
import {VitalSignType} from '../interfaces/vital-signs/vital-sign-type';
import {TtLiveActivityEntry} from '../interfaces/time-tracking/tt-live-activity-entry';
import {PrType} from '../interfaces/process-reports/pr-type';
import {TtDeviationReason} from '../interfaces/time-tracking/tt-deviation-reason';

interface TableEntry {
  [tableName: string]: string
}

class DBService extends Dexie {

  public storage!: Table<StorageItem, string>;
  public syncStatusItems!: Table<SyncStatusItem, number>;
  public serviceFacilities!: Table<ServiceFacility, number>;
  public customers!: Table<Customer, number>;
  public syncTimes!: Table<SyncTime, number>;
  public services!: Table<Service, number>;
  public employees!: Table<Employee, number>;
  public reRelationTypes!: Table<ReRelationType, number>;
  public products!: Table<Product, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Wound documentation
  // -------------------------------------------------------------------------------------------------------------------
  public wmWoundQuestions!: Table<WmWoundQuestion, number>;
  public wmWoundLocations!: Table<WmWoundLocation, number>;
  public wmWoundFinishedReasons!: Table<WmWoundFinishedReason, number>;

  public wmWoundDocumentations!: Table<WmWoundDocumentation, string>;
  public wmWoundDocumentationEntries!: Table<WmWoundDocumentationEntry, string>;
  public wmWoundAnswers!: Table<WmWoundAnswer, string>;
  public wmWoundImages!: Table<WmWoundImage, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // Care
  // -------------------------------------------------------------------------------------------------------------------
  public cdMdsAreas!: Table<CdMdsArea, number>;
  public cdAimEvaluationReasons!: Table<CdAimEvaluationReason, number>;
  public cdAimEvaluationScales!: Table<CdAimEvaluationScale, number>;
  public cdAimEvaluationOrderOptions!: Table<CdAimEvaluationOrderOption, number>;

  public cdRais!: Table<CdRai, string>;
  public cdMdsAnswers!: Table<CdMdsAnswer, string>;
  public cdCarePlans!: Table<CdCarePlan, number>;
  public cdAimEvaluations!: Table<CdAimEvaluation, string>;
  public cdAddedAims!: Table<CdAddedAim, number>;
  // ---------------------------------------------------------------------------------------------------------------
  // Time tracking
  // ---------------------------------------------------------------------------------------------------------------
  public vehicles!: Table<Vehicle, number>;
  public ttTravelEntries!: Table<TtTravelEntry, string>;
  public ttActivityTypes!: Table<TtActivityType, number>;
  public ttActivityEntries!: Table<TtActivityEntry, string>;
  public ttWorkingTimeEntries!: Table<TtWorkingTimeEntry, number>;
  public ttWorkingTimeTypes!: Table<TtWorkingTimeType, number>;
  public ttProductEntries!: Table<TtProductEntry, string>;
  public ttLiveActivityEntries!: Table<TtLiveActivityEntry, string>;
  public ttDeviationReasons!: Table<TtDeviationReason, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Employees roster
  // -------------------------------------------------------------------------------------------------------------------
  public employeeRosters!: Table<EmployeeRoster, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Operating plan
  // -------------------------------------------------------------------------------------------------------------------
  public opOperatingPlanItems!: Table<OpOperatingPlanItem, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Error handling
  // -------------------------------------------------------------------------------------------------------------------
  public errorLogs!: Table<ErrorLog, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // Process reports
  // -------------------------------------------------------------------------------------------------------------------
  public prProcessReports!: Table<PrProcessReport, string>;
  public prTypes!: Table<PrType, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // Instructions
  // -------------------------------------------------------------------------------------------------------------------
  public instructions!: Table<Instruction, number>;

  // -------------------------------------------------------------------------------------------------------------------
  // customer documents
  // -------------------------------------------------------------------------------------------------------------------
  public customerDocumentTypes!: Table<CustomerDocumentType, number>;
  public customerDocuments!: Table<CustomerDocument, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // product management
  // -------------------------------------------------------------------------------------------------------------------
  public prodRequests!: Table<ProdRequest, string>;

  // -------------------------------------------------------------------------------------------------------------------
  // vital sign
  // -------------------------------------------------------------------------------------------------------------------
  public vitalSignContexts!: Table<VitalSignContext, number>;
  public vitalSignTypes!: Table<VitalSignType, number>;
  public vitalSignOptions!: Table<VitalSignOption, number>;
  public vitalSignThresholds!: Table<VitalSignThreshold, number>;
  public vitalSignEntries!: Table<VitalSignEntry, string>;

  /**
   *
   */
  constructor() {
    super('Tables');
    const tables: TableEntry = {
      storage: '&key',
      syncStatusItems: '++id, lastSync, tableName, &primaryKey, isSynced, [tableName+isSynced], deleted, hasConflict',
      serviceFacilities: '&id',
      customers: '&id, serviceFacilityId, fullName',
      audioRecords: '++id',
      syncTimes: '++id, tableName',
      services: '&id',
      employees: '&id, fullName, serviceFacilityId',
      reRelationTypes: '&id',
      products: '&id',
      // ---------------------------------------------------------------------------------------------------------------
      // Wound documentation
      // ---------------------------------------------------------------------------------------------------------------
      wmWoundQuestions: '&id',
      wmWoundLocations: '&id',
      wmWoundFinishedReasons: '&id',
      wmWoundDocumentations: '&uuid, customerId, createdDate',
      wmWoundDocumentationEntries: '&uuid, wmWoundDocumentationUuid, createdDate, created',
      wmWoundAnswers: '&uuid, wmWoundDocumentationEntryUuid, wmWoundQuestionId',
      wmWoundImages: '&uuid, wmWoundDocumentationEntryUuid, created',
      // ---------------------------------------------------------------------------------------------------------------
      // Care
      // ---------------------------------------------------------------------------------------------------------------
      cdMdsAreas: '&id',
      cdAimEvaluationReasons: '&id',
      cdAimEvaluationOrderOptions: '&id',
      cdAimEvaluationScales: '&value',
      cdRais: '&uuid, customerId, date',
      cdMdsAnswers: '&uuid, cdRaiUuid, cdRaiMdsQuestionId',
      cdCarePlans: '&id, customerId, createdDate',
      cdAimEvaluations: '&uuid, cdAddedAimId',
      cdAddedAims: '&id, cdCarePlanId',
      // ---------------------------------------------------------------------------------------------------------------
      // Time tracking
      // ---------------------------------------------------------------------------------------------------------------
      vehicles: '&id, serviceFacilityId, licensePlate',
      ttTravelEntries: '&uuid, date',
      ttActivityTypes: '&id, sort, name, [sort+name]',
      ttWorkingTimeTypes: '&id',
      ttActivityEntries: '&uuid, timeFrom, timeTo, opOperatingPlanItemId, ttWorkingTimeTypeId',
      ttWorkingTimeEntries: '&id, timeFrom, timeTo',
      ttProductEntries: '&uuid, ttActivityEntryUuid, employeeId',
      ttLiveActivityEntries: '&uuid',
      ttDeviationReasons: '&id',
      // ---------------------------------------------------------------------------------------------------------------
      // Process reports
      // ---------------------------------------------------------------------------------------------------------------
      prProcessReports: '&uuid, &id, customerId, created',
      prTypes: '&id, forCustomers, forEmployees, forServiceFacilities, isDefault',
      // ---------------------------------------------------------------------------------------------------------------
      // Employees roster
      // ---------------------------------------------------------------------------------------------------------------
      employeeRosters: '&id, date',
      // ---------------------------------------------------------------------------------------------------------------
      // Operating plan
      // ---------------------------------------------------------------------------------------------------------------
      opOperatingPlanItems: '++id, employeeId, start',
      // ---------------------------------------------------------------------------------------------------------------
      // Instructions
      // ---------------------------------------------------------------------------------------------------------------
      instructions: '&id, created',
      // ---------------------------------------------------------------------------------------------------------------
      // Instructions
      // ---------------------------------------------------------------------------------------------------------------
      customerDocumentTypes: '&id, name',
      customerDocuments: '&uuid, customerId, created',
      // ---------------------------------------------------------------------------------------------------------------
      // product management
      // ---------------------------------------------------------------------------------------------------------------
      prodRequests: '&uuid, customerId, created',
      // ---------------------------------------------------------------------------------------------------------------
      // vital signs
      // ---------------------------------------------------------------------------------------------------------------
      vitalSignContexts: '&id, vitalSignTypeId',
      vitalSignTypes: '&id',
      vitalSignOptions: '&id, vitalSignTypeId',
      vitalSignThresholds: '&id, customerId, vitalSignTypeId, [customerId+vitalSignTypeId]',
      vitalSignEntries: '&uuid, customerId, vitalSignTypeId, [customerId+vitalSignTypeId]',
      // ---------------------------------------------------------------------------------------------------------------
      // Error handling
      // ---------------------------------------------------------------------------------------------------------------
      errorLogs: '&uuid'
    };
    // TODO: Add tables here
    this.version(41).stores(tables);
  }

  /**
   *
   * @param tableName
   */
  public getSyncTableByTableName(tableName: TableName): Table<SyncItem, string> {
    if(this.hasOwnProperty(tableName)) {
      // @ts-ignore
      return this[tableName];
    }
    return null;
  }

}

export const db = new DBService();
